import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FsLightbox from 'fslightbox-react';
import clsx from 'clsx';

import ImageWithLoader from '@components/_shared/imageWithLoader';
import { BaseComponentProps } from '@utils/types/baseComponents';
import { useIsMobile } from '@utils/hooks/useIsMobile';

import styles from './styles.module.scss';

type Props = {
  images: string[];
  slideToOpen?: number;
} & BaseComponentProps;

Gallery.defaultProps = {
  images: [],
};

export function Gallery(props: Props) {
  const { containerStyle, images, slideToOpen } = props;
  const isMobile = useIsMobile();
  const [lightboxController, setLightboxController] = useState({
    toggler: false,
    slide: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (slideToOpen) {
      setLightboxController((value) => ({
        toggler: !value.toggler,
        slide: slideToOpen,
      }));
    }
  }, [slideToOpen]);

  const openImageViewer = useCallback((index) => {
    setLightboxController((value) => ({
      toggler: !value.toggler,
      slide: index + 1,
    }));
  }, []);

  const onImageInGalleryClick = (image: string) => {
    window.open(image, '_blank');
  };

  const imagesList = useMemo(() => {
    const list = [];
    for (let i = 0; i < images.length; ++i) {
      const key = `${i} - ${images[i]}`;
      if (i % 3 === 0) {
        list.push(
          <div key={key} className={styles.bigImage}>
            <ImageWithLoader
              onClick={() => openImageViewer(i)}
              key={key}
              src={images[i]}
              alt={`image-${i}`}
              layout="fixed"
              height="580px"
              width="855px"
            />
          </div>
        );
      } else if (i < images.length) {
        const nextIndex = i + 1;
        list.push(
          <div key={key} className={styles.smallImageContainer}>
            <ImageWithLoader
              onClick={() => openImageViewer(i - 1)}
              key={key}
              src={images[i]}
              alt={`image-${i}`}
              layout="fixed"
              height="280px"
              width="420px"
              containerStyle={styles.smallImage}
            />
            {!!images[nextIndex] && (
              <ImageWithLoader
                onClick={() => openImageViewer(nextIndex)}
                key={nextIndex}
                src={images[nextIndex]}
                alt={`image-${nextIndex}`}
                layout="fixed"
                height="280px"
                width="420px"
                containerStyle={styles.smallImage}
              />
            )}
          </div>
        );
        i++;
      }
    }
    return list;
  }, [images, openImageViewer, isMobile]);

  useEffect(() => {
    if (imagesList.length && containerRef.current) {
      containerRef.current.scrollLeft = 0;
    }
  }, [imagesList.length]);

  return (
    <div
      className={clsx([styles.galleryContainer, containerStyle])}
      ref={containerRef}
    >
      {imagesList}
      <FsLightbox
        toggler={lightboxController.toggler}
        // @ts-ignore
        sources={images.map((i, key) => (
          <div key={`lightbox-image--${key}`}>
            <ImageWithLoader
              onClick={() => onImageInGalleryClick(i)}
              src={i}
              alt="Listing"
              layout="fixed"
              height={isMobile ? '259px' : '580px'}
              width={isMobile ? '391px' : '855px'}
            />
          </div>
        ))}
        slide={lightboxController.slide}
      />
    </div>
  );
}
